import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Paperclip } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FormAlert } from "@/components/common/Alert";
import { useCatalogue } from "@/context/CatalogueContext";
import { api, errorMessage } from "@/lib/api";
import { agentName } from "@/lib/catalogue";
import { cn } from "@/lib/utils";

export const FEEDBACK_TYPES = [
  ["bug", "Bug"], ["wrong_output", "Wrong output"], ["guardrail", "Guardrail concern"], ["idea", "Idea"], ["praise", "Praise"],
];
export const TYPE_LABEL = Object.fromEntries(FEEDBACK_TYPES);
export const STATUS_LABEL = { new: "New", acknowledged: "Acknowledged", in_progress: "In progress", fixed: "Fixed", declined: "Declined" };

export function useAboutResolver() {
  const { agentsByKey, workflowsByKey, teamsById } = useCatalogue();
  return (id) => {
    if (!id || id === "portal") return "portal";
    if (agentsByKey[id]) return `agent:${id}`;
    if (workflowsByKey[id]) return `workflow:${id}`;
    if (teamsById[id]) return `team:${id}`;
    return "portal";
  };
}

const EMPTY = { about: "portal", type: "idea", rating: 0, text: "", run_link: "" };

export const FeedbackDialog = ({ open, onOpenChange, defaultAbout = "portal", onSent }) => {
  const { agents, workflows, teams } = useCatalogue();
  const fileRef = useRef(null);
  const [form, setForm] = useState(EMPTY);
  const [shot, setShot] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setForm({ ...EMPTY, about: defaultAbout }); setShot(null); setError(""); } }, [open, defaultAbout]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const upload = async (file) => {
    if (!file) return;
    const body = new FormData();
    body.append("file", file);
    try {
      const { data } = await api.post("/feedback/screenshot", body);
      setShot({ id: data.file_id, name: file.name });
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.rating) return setError("Choose a rating from 1 to 5.");
    setBusy(true);
    setError("");
    const [about_type, about_id] = form.about.includes(":") ? form.about.split(":") : ["portal", null];
    try {
      const { data } = await api.post("/feedback", { about_type, about_id, type: form.type, rating: form.rating, text: form.text, run_link: form.run_link, screenshot_file_id: shot?.id || null });
      toast.success(form.type === "guardrail" ? "Thank you. The Owner has been alerted straight away." : "Thank you. Your feedback is with the Owner.");
      onSent?.(data);
      onOpenChange(false);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto rounded-xl sm:max-w-xl" data-testid="feedback-dialog">
        <DialogHeader>
          <DialogTitle>Give feedback</DialogTitle>
          <DialogDescription>Goes to the hub Owner. You can follow its status on My feedback.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <FormAlert testid="feedback-error">{error}</FormAlert>
          <div className="space-y-2">
            <Label htmlFor="fb-about">About</Label>
            <Select value={form.about} onValueChange={(v) => set("about", v)}>
              <SelectTrigger id="fb-about" className="h-10 rounded-xl" data-testid="feedback-about-select"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-80 rounded-xl">
                <SelectItem value="portal">The portal</SelectItem>
                <SelectGroup><SelectLabel>Teams</SelectLabel>{teams.map((t) => <SelectItem key={t.id} value={`team:${t.id}`}>{t.section || t.name}</SelectItem>)}</SelectGroup>
                <SelectGroup><SelectLabel>Workflows</SelectLabel>{workflows.map((w) => <SelectItem key={w.id} value={`workflow:${w.id}`}>{w.name}</SelectItem>)}</SelectGroup>
                <SelectGroup><SelectLabel>Agents</SelectLabel>{agents.map((a) => <SelectItem key={a.emp_no} value={`agent:${a.emp_no}`}>{agentName(a)}</SelectItem>)}</SelectGroup>
              </SelectContent>
            </Select>
          </div>
          <fieldset>
            <legend className="text-sm font-medium">Type</legend>
            <div className="mt-2 flex flex-wrap gap-1.5" role="radiogroup">
              {FEEDBACK_TYPES.map(([v, l]) => (
                <button key={v} type="button" role="radio" aria-checked={form.type === v} onClick={() => set("type", v)} data-testid={`feedback-type-${v}`}
                  className={cn("rounded-full border px-3 py-1.5 text-sm transition-quick hover:bg-muted", form.type === v && (v === "guardrail" ? "border-destructive bg-destructive/10 font-medium" : "border-foreground font-medium"))}>{l}</button>
              ))}
            </div>
            {form.type === "guardrail" && <p className="mt-2 text-xs text-destructive">The Owner is emailed as soon as you send this.</p>}
          </fieldset>
          <fieldset>
            <legend className="text-sm font-medium">Rating</legend>
            <div className="mt-2 flex gap-1.5" role="radiogroup">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} type="button" role="radio" aria-checked={form.rating === n} onClick={() => set("rating", n)} data-testid={`feedback-rating-${n}`}
                  className={cn("h-9 w-9 rounded-lg border text-sm transition-quick hover:bg-muted", form.rating === n && "border-foreground font-semibold")}>{n}</button>
              ))}
            </div>
          </fieldset>
          <div className="space-y-2">
            <Label htmlFor="fb-text">What happened</Label>
            <Textarea id="fb-text" value={form.text} maxLength={2000} rows={5} onChange={(e) => set("text", e.target.value)} className="rounded-xl" data-testid="feedback-text-input" />
            <p className="text-right text-xs text-muted-foreground">{form.text.length} of 2,000</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="fb-link">Link to the run (optional)</Label>
            <Input id="fb-link" value={form.run_link} maxLength={500} placeholder="https://" onChange={(e) => set("run_link", e.target.value)} data-testid="feedback-link-input" />
          </div>
          <div className="flex items-center gap-3">
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="sr-only" onChange={(e) => upload(e.target.files?.[0])} data-testid="feedback-screenshot-input" />
            <Button type="button" variant="secondary" size="sm" onClick={() => fileRef.current?.click()} data-testid="feedback-screenshot-button"><Paperclip className="!h-4 !w-4" /> {shot ? "Replace screenshot" : "Add a screenshot"}</Button>
            {shot && <span className="truncate text-xs text-muted-foreground" data-testid="feedback-screenshot-name">{shot.name}</span>}
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} data-testid="feedback-cancel">Cancel</Button>
            <Button type="submit" disabled={busy || !form.text.trim()} data-testid="feedback-submit">{busy ? "Sending" : "Send feedback"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
